import { UseMutationOptions, useMutation, useQueryClient } from 'react-query';

import { fetchJson } from '@zapier/toolbox-browser-fetch';

import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { useNotify } from 'app/developer-v3/hooks';
import { getQueryKey } from './useQueryIntegrationMaintenanceConfig';

import { formatIntegrationMaintenanceConfig } from './utils';
import { MUTATION_KEY } from './constants';

import type { IntegrationMaintenanceConfig } from './types';

export function formatResponseBody(body: any): IntegrationMaintenanceConfig {
  return formatIntegrationMaintenanceConfig(body);
}

function formatToRequestBody(config: Args) {
  return { allow_internal_maintenance: config.allowInternalMaintenance };
}

type Args = Pick<IntegrationMaintenanceConfig, 'allowInternalMaintenance'>;

function useMutationUpdateIntegrationMaintenanceConfig(
  args: { integrationId: number },
  options?: UseMutationOptions<void, Error, Args, IntegrationMaintenanceConfig>
) {
  const { integrationId } = args;
  const notify = useNotify();

  const queryClient = useQueryClient();
  const queryKey = getQueryKey({ integrationId });
  const mutationKey = MUTATION_KEY;
  const url = new URL(
    `api/platform/cli/apps/${encodeURIComponent(
      integrationId
    )}/maintenance-config`,
    ZAPIER_APP_BASE_URL
  );

  const mutation = useMutation({
    mutationKey,
    async mutationFn(config: Args) {
      try {
        const response = await fetchJson(url.href, {
          body: JSON.stringify(formatToRequestBody(config)),
          method: 'PATCH',
        });
        return response;
      } catch (error) {
        throw formatResponseBody(JSON.parse(error.responseText));
      }
    },
    onError: (error: any, _variables) => {
      notify.failure(
        'There was a problem updating the integration maintenance config.',
        mutationKey
      );
      console.error(JSON.stringify(error));
    },
    onSuccess: () => {
      notify.success('The integration maintenance config has been updated.');
      queryClient.invalidateQueries(queryKey);
    },
    ...options,
  });

  return { ...mutation, mutationKey };
}

export default useMutationUpdateIntegrationMaintenanceConfig;
